import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useParams, useNavigate } from "react-router-dom";
import { usePayment } from "../../hooks/usePayment";
import StripeErrorPage from "./StripeErrorPage";

let stripePromise;
let stripeLoadError = false;

try {
  stripePromise = await loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  );
  if (!stripePromise) {
    stripeLoadError = true;
  }
} catch (e) {
  stripeLoadError = true;
}

const CheckoutForm = ({ listing, clientSecret, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const card = elements.getElement(CardElement);

    const { error: stripeError, paymentIntent } =
      await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: card,
          billing_details: {
            email: listing.user_email || listing.contact_email,
          },
        },
      });

    if (stripeError) {
      setError(stripeError.message);
      setIsLoading(false);
    } else {
      setSucceeded(true);
      setIsLoading(false);
      onSuccess(paymentIntent);
    }
  };

  const cardStyle = {
    style: {
      base: {
        color: "#424770",
        fontFamily: "Arial, sans-serif",
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#9e2146",
        iconColor: "#9e2146",
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Payment Details</h3>
        <div className="border p-4 rounded-md">
          <CardElement options={cardStyle} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {succeeded && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          Payment succeeded! Your listing is now active.
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isLoading || succeeded}
        className={`w-full py-3 px-4 rounded-md font-medium ${
          !stripe || isLoading || succeeded
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700 text-white"
        }`}
      >
        {isLoading ? "Processing..." : `Pay $${listing.amount || "10.00"}`}
      </button>
    </form>
  );
};

const PaymentPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { getPaymentIntent, confirmPayment, getPaymentStatus } = usePayment();
  const [listing, setListing] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect to error page if Stripe failed to load
  useEffect(() => {
    if (stripeLoadError) {
      navigate("/stripe-error");
    }
  }, [stripeLoadError]);

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        // Check if payment is already completed
        const statusData = await getPaymentStatus(listingId);
        if (statusData.is_paid) {
          navigate(`/payment-success/${listingId}`);
          return;
        }
        // Always get a fresh payment intent for Stripe
        const paymentData = await getPaymentIntent(listingId);
        setListing(paymentData.listing);
        setClientSecret(paymentData.payment_client_secret);
      } catch (err) {
        setError("Failed to load payment information");
        console.error("Payment data fetch error:", err);
        console.error("Error details:", err.response?.data);
      } finally {
        setLoading(false);
      }
    };

    if (listingId) {
      fetchPaymentData();
    }
  }, [listingId]);

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      await confirmPayment(listingId, paymentIntent.id);
      // Redirect to success page or listing management
      navigate(`/payment-success/${listingId}`);
    } catch (err) {
      console.error("Payment confirmation error:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (stripeLoadError) {
    return <StripeErrorPage />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Error
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Complete Your Payment
          </h1>

          {listing && (
            <div className="border-b pb-4 mb-6">
              <h2 className="text-lg font-semibold text-gray-800">
                {listing.post_title}
              </h2>
              <p className="text-gray-600 mt-2">{listing.description}</p>
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-500">Listing Fee</span>
                <span className="text-lg font-semibold">
                  ${listing.amount || "10.00"}
                </span>
              </div>
            </div>
          )}

          {clientSecret && (
            <Elements stripe={stripePromise}>
              <CheckoutForm
                listing={listing}
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
              />
            </Elements>
          )}
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>🔒 Your payment information is secure and encrypted</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
